import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { ApiError, api } from '@/api/client'
import type { PropertyCard } from '@/api/types'
import { money, nights } from '@/lib/format'
import * as d from '@/lib/dates'

import type { Search } from './SearchBar'
import { SearchBar } from './SearchBar'

interface Props {
  search: Search
  onSearch(next: Search): void
}

export function CatalogPage({ search, onSearch }: Props) {
  const hasDates = Boolean(search.checkIn && search.checkOut)

  const catalog = useQuery({
    queryKey: ['catalog', search],
    queryFn: () =>
      api.catalog({
        check_in: hasDates ? search.checkIn : undefined,
        check_out: hasDates ? search.checkOut : undefined,
        adults: search.adults,
        children: search.children,
      }),
  })

  const nightCount = hasDates ? d.nightsBetween(search.checkIn, search.checkOut) : 0

  return (
    <>
      <section className="hero">
        <h1 className="hero__title display">
          Жильё у моря — <em>напрямую</em>
        </h1>
        <p className="hero__lead">
          Бронирование без посредников и их комиссии. Пишете хозяину, а не колл-центру.
        </p>
        <SearchBar value={search} onChange={onSearch} />
      </section>

      <section className="section">
        {catalog.isLoading && <p className="muted">Ищем варианты…</p>}

        {catalog.isError && (
          <p className="error">
            {catalog.error instanceof ApiError
              ? catalog.error.message
              : 'Не удалось загрузить список'}
          </p>
        )}

        {catalog.data && catalog.data.length === 0 && (
          <div className="empty">
            <p className="empty__title">На эти даты свободных мест нет</p>
            <p className="muted">Попробуйте сдвинуть даты или уменьшить число гостей.</p>
          </div>
        )}

        {catalog.data && catalog.data.length > 0 && (
          <>
            <h2 className="section__title">
              {hasDates ? `Свободно на ${nights(nightCount)}` : 'Куда поехать'}
            </h2>
            <ul className="cards">
              {catalog.data.map((item) => (
                <li key={item.id}>
                  <Card item={item} search={search} nightCount={nightCount} />
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </>
  )
}

function Card({
  item,
  search,
  nightCount,
}: {
  item: PropertyCard
  search: Search
  nightCount: number
}) {
  const query = new URLSearchParams()
  if (search.checkIn) query.set('check_in', search.checkIn)
  if (search.checkOut) query.set('check_out', search.checkOut)
  query.set('adults', String(search.adults))
  if (search.children) query.set('children', String(search.children))

  return (
    <Link className="card" to={`/hotel/${item.slug}?${query}`}>
      <div className="card__photo">
        {item.cover ? (
          <img src={item.cover.url} alt={item.cover.alt ?? item.name} loading="lazy" />
        ) : (
          <span className="card__nophoto">Фотографий пока нет</span>
        )}
      </div>

      <div className="card__body">
        <h3 className="card__name">{item.name}</h3>
        <p className="card__place">
          {[item.city, item.star_rating ? '★'.repeat(item.star_rating) : null]
            .filter(Boolean)
            .join(' · ')}
        </p>

        {item.highlights.length > 0 && (
          <ul className="tags">
            {item.highlights.map((h) => (
              <li key={h.name} className="tag">
                {h.name}
              </li>
            ))}
          </ul>
        )}

        {item.min_price !== null && (
          <p className="card__price">
            <strong>{money(item.min_price, item.currency)}</strong>
            <span className="muted"> за {nights(nightCount)}</span>
          </p>
        )}
      </div>
    </Link>
  )
}
